import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setActiveSubjectHeader, subjectAPI } from '../services/api';
import { SubjectContext, SubjectContextValue } from './subjectContextValue';
import { DEFAULT_SUBJECT_ID, StartMode, SubjectId, SubjectsPayload, YearLevel, enrolledSubjects } from './types';

export { useActiveSubjectId } from './subjectContextValue';

setActiveSubjectHeader(DEFAULT_SUBJECT_ID);

export const SubjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [payload, setPayload] = useState<SubjectsPayload | null>(null);
  const [activeId, setActiveId] = useState<SubjectId>(DEFAULT_SUBJECT_ID);

  const activate = useCallback((id: SubjectId) => {
    setActiveSubjectHeader(id);
    setActiveId(id);
  }, []);

  const refresh = useCallback(async () => {
    setPayload(await subjectAPI.list());
  }, []);

  useEffect(() => {
    if (userId === null) {
      setPayload(null);
      activate(DEFAULT_SUBJECT_ID);
      return;
    }
    let cancelled = false;
    subjectAPI
      .list()
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        activate(data.last_used);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [userId, activate]);

  const switchSubject = useCallback(
    (id: SubjectId) => {
      if (id === activeId) return;
      activate(id);
      subjectAPI.update(id, { opened: true }).then(setPayload).catch(() => undefined);
    },
    [activeId, activate],
  );

  const addSubject = useCallback(
    async (id: SubjectId, yearLevel: YearLevel, startMode: StartMode) => {
      setPayload(await subjectAPI.add(id, { year_level: yearLevel, start_mode: startMode }));
    },
    [],
  );

  const setHidden = useCallback(
    async (id: SubjectId, hidden: boolean) => {
      const data = await subjectAPI.update(id, { hidden });
      setPayload(data);
      if (hidden && id === activeId) activate(data.last_used);
    },
    [activeId, activate],
  );

  const value = useMemo<SubjectContextValue>(() => {
    const all = payload ? enrolledSubjects(payload) : [];
    return {
      payload,
      activeId,
      active: all.find((item) => item.id === activeId) ?? null,
      visible: all.filter((item) => !item.enrolment.hidden),
      hidden: all.filter((item) => item.enrolment.hidden),
      catalogue: payload?.catalogue ?? [],
      switchSubject,
      addSubject,
      setHidden,
      refresh,
    };
  }, [payload, activeId, switchSubject, addSubject, setHidden, refresh]);

  return <SubjectContext.Provider value={value}>{children}</SubjectContext.Provider>;
};

export const useSubjects = (): SubjectContextValue => {
  const context = useContext(SubjectContext);
  if (!context) throw new Error('useSubjects must be used within a SubjectProvider');
  return context;
};

export const METHODS_COLOUR = '#c45200';

export const useSubjectColour = (): string => useSubjects().active?.colour ?? METHODS_COLOUR;

// --subject feeds the `subject-*` Tailwind palette, so subject-scoped pages take the active subject's accent.
export const SubjectScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeId } = useSubjects();
  const colour = useSubjectColour();
  return (
    <div className="App" key={activeId} style={{ '--subject': colour } as React.CSSProperties}>
      {children}
    </div>
  );
};

const scrollMemory = new Map<string, number>();

export const useRememberedScroll = (page: string, ready: boolean) => {
  const { activeId } = useSubjects();
  const key = `${activeId}:${page}`;

  useLayoutEffect(() => {
    if (ready) window.scrollTo(0, scrollMemory.get(key) ?? 0);
  }, [key, ready]);

  useEffect(() => {
    if (!ready) return undefined;
    const save = () => scrollMemory.set(key, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [key, ready]);
};
